"use client";

import * as TagsInputPrimitive from "@diceui/tags-input";
import type { VariantProps } from "class-variance-authority";
import type { ComponentProps, ReactNode } from "react";

import { cn } from "../../../utils";
import { XIcon } from "../../icons";
import { inputContainerStyles, inputStyles } from "../style";

export type InputTagsProps = ComponentProps<typeof TagsInputPrimitive.Root> &
  VariantProps<typeof inputStyles> &
  VariantProps<typeof inputContainerStyles> & {
    containerClassName?: string;
    prefix?: ReactNode;
    suffix?: ReactNode;
    placeholder?: string;
    isInvalid?: boolean;
  };

const InputTags = ({
  className,
  variant = "outline",
  color = "primary",
  containerClassName,
  prefix,
  suffix,
  value,
  placeholder,
  isInvalid,
  ...props
}: InputTagsProps) => {
  return (
    <TagsInputPrimitive.Root
      data-slot="input-tags-root"
      className={cn(inputContainerStyles({ variant, color }), "py-[9px]", containerClassName)}
      value={value}
      data-invalid={isInvalid}
      {...props}
    >
      {prefix}

      <div className="flex flex-wrap gap-1.5">
        {value?.map(item => (
          <TagsInputPrimitive.Item
            key={item}
            data-slot="input-tags-item"
            className={cn(
              "data-editing:ring-ring box-border inline-flex h-5 max-w-[calc(100%-8px)] items-center gap-0.5 rounded border border-neutral-80 bg-transparent px-1 text-sm focus:outline-hidden data-disabled:cursor-not-allowed data-disabled:opacity-50 data-editable:select-none data-editing:bg-transparent data-editing:ring-1 [&[data-highlighted]:not([data-editing])]:bg-neutral-96 [&[data-highlighted]:not([data-editing])]:text-neutral-10"
            )}
            value={item}
          >
            <TagsInputPrimitive.ItemText className="truncate">{item}</TagsInputPrimitive.ItemText>
            <TagsInputPrimitive.ItemDelete className="flex size-4 shrink-0 items-center justify-center rounded-sm text-neutral-50 transition-all hover:text-neutral-40">
              <XIcon className="size-3.5" />
            </TagsInputPrimitive.ItemDelete>
          </TagsInputPrimitive.Item>
        ))}

        <TagsInputPrimitive.Input
          data-slot="input-tags-input"
          className={cn(inputStyles({ variant }), "w-unset flex-1")}
          placeholder={value?.length === 0 ? placeholder : undefined}
        />
      </div>

      {suffix}
    </TagsInputPrimitive.Root>
  );
};

export default InputTags;
